import clsx from 'clsx';
import React, { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Button,
  HUE_TINT,
  IconButton,
  IconCalendar,
  IconClose,
  IconGrid,
  IconPhone,
  IconReceipt,
  IconSparkle,
  IconTask,
  IconVoicemail,
  OVERLAY_Z,
  useEscape,
  useFocusTrap,
  useScrollLock,
  type Hue,
} from '../ui/v2';
import { api, apiEndpoints } from '../utils/apiService';

/** Queued server-side for every user who existed when the new look shipped. */
export const NEW_LOOK_ANNOUNCEMENT = 'dashboard-v2-welcome';

const dismissedKey = (userId: number) =>
  `av2.announcement.${NEW_LOOK_ANNOUNCEMENT}.${userId}`;

function dismissedHere(userId: number): boolean {
  try {
    return localStorage.getItem(dismissedKey(userId)) === '1';
  } catch {
    return false;
  }
}

/**
 * Shown once per user. The server list is the source of truth across
 * devices; the local flag covers the cached user info until it refreshes,
 * and a dismiss request that failed.
 */
function useNewLookAnnouncement() {
  const { user } = useAuth();
  const [closed, setClosed] = useState(false);
  const pending =
    !!user &&
    !closed &&
    (user.pending_announcements ?? []).includes(NEW_LOOK_ANNOUNCEMENT) &&
    !dismissedHere(user.id);
  const dismiss = () => {
    setClosed(true);
    if (!user) return;
    try {
      localStorage.setItem(dismissedKey(user.id), '1');
    } catch {
      /* the server dismissal still applies */
    }
    void api
      .post(apiEndpoints.dismissAnnouncement(NEW_LOOK_ANNOUNCEMENT))
      .catch(() => undefined);
  };
  return { open: pending, firstName: user?.first_name || '', dismiss };
}

const HIGHLIGHTS: {
  hue: Hue;
  icon: React.ReactNode;
  title: string;
  body: string;
}[] = [
  {
    hue: 'accent',
    icon: <IconSparkle size={16} />,
    title: 'Arthur’s report',
    body: 'One line on what Arthur did for you this month, with calls, reservations and orders right underneath.',
  },
  {
    hue: 'amber',
    icon: <IconTask size={16} />,
    title: 'Action items in one list',
    body: 'Voicemails, texts and inquiries that need a person, most urgent first, each a click away.',
  },
  {
    hue: 'sky',
    icon: <IconGrid size={16} />,
    title: 'A simpler sidebar',
    body: 'Every page in one place, with your store at the top. Collapse it when you want more room.',
  },
];

/** A miniature of the new dashboard, peeking up from the bottom of the hero. */
const DashboardSketch: React.FC = () => {
  const figures: { hue: Hue; icon: React.ReactNode; width: string }[] = [
    { hue: 'sky', icon: <IconPhone size={10} />, width: 'w-9' },
    { hue: 'violet', icon: <IconCalendar size={10} />, width: 'w-7' },
    { hue: 'teal', icon: <IconReceipt size={10} />, width: 'w-8' },
    { hue: 'rose', icon: <IconVoicemail size={10} />, width: 'w-6' },
  ];
  return (
    <div
      aria-hidden
      className="relative mx-auto -mb-px w-full max-w-[440px] translate-y-px rounded-t-[14px] border border-b-0 border-white/10 bg-v2-surface px-4 pb-4 pt-3.5 shadow-[0_-12px_40px_-12px_rgb(0_0_0/0.45)]"
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="h-1.5 w-14 rounded-full bg-v2-fg/80" />
        <span className="h-1.5 w-10 rounded-full bg-v2-line-strong" />
      </div>
      <div className="grid grid-cols-4 overflow-hidden rounded-[9px] border border-v2-line">
        {figures.map((f, i) => (
          <div
            key={i}
            className={clsx(
              'flex flex-col gap-1.5 px-2.5 py-2',
              i > 0 && 'border-l border-v2-line',
            )}
          >
            <span
              className={clsx(
                'inline-flex h-[18px] w-[18px] items-center justify-center rounded-[5px]',
                HUE_TINT[f.hue],
              )}
            >
              {f.icon}
            </span>
            <span
              className={clsx('h-2.5 rounded-[3px] bg-v2-fg/85', f.width)}
            />
            <span className="h-1 w-10 rounded-full bg-v2-line-strong" />
          </div>
        ))}
      </div>
      <div className="mt-3 space-y-1.5">
        {(['amber', 'accent'] as Hue[]).map((hue, i) => (
          <div
            key={hue}
            className="flex items-center gap-2 rounded-[7px] bg-v2-sunken px-2.5 py-1.5"
          >
            <span className={clsx('h-1.5 w-1.5 rounded-full', HUE_TINT[hue])} />
            <span
              className={clsx(
                'h-1.5 rounded-full bg-v2-fg/60',
                i === 0 ? 'w-28' : 'w-20',
              )}
            />
            <span className="ml-auto h-1.5 w-8 rounded-full bg-v2-line-strong" />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * The one-time welcome to the new-look dashboard. Rendered by the V2 frame,
 * so a user on the legacy frame never sees it.
 */
const NewLookWelcome: React.FC = () => {
  const { open, firstName, dismiss } = useNewLookAnnouncement();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useEscape(open, dismiss);
  useFocusTrap(open, ref);
  useScrollLock(open);
  if (!open) return null;

  return createPortal(
    <div
      className="av2 fixed inset-0 flex items-start justify-center overflow-y-auto bg-transparent px-4 py-[6vh] sm:items-center"
      style={{ zIndex: OVERLAY_Z.dialog }}
    >
      <div
        aria-hidden
        className="v2-fade-in fixed inset-0 bg-v2-fg/40 backdrop-blur-[2px] dark:bg-black/65"
        onClick={dismiss}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        className="v2-rise-in relative w-full max-w-[580px] overflow-clip rounded-[22px] bg-v2-surface shadow-v2-dialog outline-none"
      >
        <div className="v2-hero relative px-6 pt-7 sm:px-9 sm:pt-9">
          <div className="v2-inverse">
            <IconButton
              label="Close"
              onClick={dismiss}
              className="absolute right-3 top-3 sm:right-4 sm:top-4"
            >
              <IconClose />
            </IconButton>
            <p className="mb-3 text-v2-11 font-semibold uppercase tracking-[0.16em] text-v2-accent">
              {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
            </p>
            <h2
              id={titleId}
              className="text-balance font-v2-display text-[30px] font-medium leading-[34px] text-v2-fg sm:text-[36px] sm:leading-[40px]"
            >
              Welcome to your{' '}
              <span className="whitespace-nowrap">new-look</span> dashboard
            </h2>
            <p
              id={bodyId}
              className="mt-3 max-w-[440px] text-v2-14 leading-[22px] text-v2-fg-2"
            >
              Same Arthur, a clearer home. What Arthur handled, what needs you,
              and how the month is going now live on one page.
            </p>
          </div>
          <div className="mt-7">
            <DashboardSketch />
          </div>
        </div>

        <ul className="space-y-4 px-6 pb-2 pt-6 sm:px-9">
          {HIGHLIGHTS.map((h) => (
            <li key={h.title} className="flex gap-3.5">
              <span
                aria-hidden
                className={clsx(
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]',
                  HUE_TINT[h.hue],
                )}
              >
                {h.icon}
              </span>
              <div className="min-w-0">
                <p className="text-v2-14 font-semibold text-v2-fg">{h.title}</p>
                <p className="mt-0.5 text-v2-13 leading-5 text-v2-fg-3">
                  {h.body}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <footer className="mt-4 flex flex-col-reverse gap-3 border-t border-v2-line px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-9">
          <p className="text-v2-12 text-v2-fg-3">
            How Arthur answers your calls hasn’t changed.
          </p>
          <Button
            variant="primary"
            data-autofocus="true"
            onClick={() => {
              dismiss();
              navigate('/');
            }}
          >
            Take me to my dashboard
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default NewLookWelcome;
